import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import {
  Key,
  Search,
  Filter,
  BarChart3,
  Activity,
  AlertCircle,
  Eye,
  TrendingUp
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const AdminApiKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [selectedKey, setSelectedKey] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [keyUsage, setKeyUsage] = useState(null);

  const fetchApiKeys = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined
      };

      const response = await adminAPI.getApiKeys(params);
      setApiKeys(response.data.data.keys);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.total,
        totalPages: response.data.data.totalPages
      }));
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, statusFilter]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);


  const viewKeyDetails = async (apiKey) => {
    setSelectedKey(apiKey);
    setShowDetailsModal(true);
    
    try {
      // Fetch detailed usage for this specific key
      // Note: This would require a new admin endpoint for specific key usage
      setKeyUsage({
        totalRequests: apiKey.totalRequestsToday || 0,
        successRate: apiKey.successRateToday || 0,
        errorCount: apiKey.errorCountToday || 0,
        lastUsed: apiKey.lastUsed
      });
    } catch (error) {
      console.error('Error fetching key usage:', error);
      setKeyUsage(null);
    }
  };

  const getTopApiKeysData = () => {
    return apiKeys
      .filter(key => key.requestsToday > 0)
      .sort((a, b) => (b.requestsToday || 0) - (a.requestsToday || 0))
      .slice(0, 10)
      .map(key => ({
        name: `${key.name} (${key.username})`,
        requests: key.requestsToday || 0,
        success: key.successToday || 0,
        errors: (key.requestsToday || 0) - (key.successToday || 0)
      }));
  };

  const filteredApiKeys = apiKeys.filter(apiKey => {
    const matchesSearch = !searchTerm || 
      apiKey.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apiKey.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apiKey.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apiKey.keyPrefix?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && apiKey.isActive) ||
      (statusFilter === 'inactive' && !apiKey.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading && apiKeys.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const topKeysData = getTopApiKeysData();

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Key className="w-8 h-8 mr-3 text-green-600" />
              API Key Management
            </h1>
            <p className="text-gray-600 mt-2">
              Monitor API key usage and manage access
            </p>
          </div>
        </div>
      </div>

      {/* Today's Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <div className="flex items-center">
            <Key className="w-6 h-6 text-blue-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Total API Keys</p>
              <p className="text-xl font-bold">{pagination.total}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <Activity className="w-6 h-6 text-green-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Active Keys</p>
              <p className="text-xl font-bold">{apiKeys.filter(k => k.isActive).length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <BarChart3 className="w-6 h-6 text-purple-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Today's Requests</p>
              <p className="text-xl font-bold">
                {apiKeys.reduce((sum, key) => sum + (key.requestsToday || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <TrendingUp className="w-6 h-6 text-orange-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Success Rate</p>
              <p className="text-xl font-bold">
                {(() => {
                  const totalRequests = apiKeys.reduce((sum, key) => sum + (key.requestsToday || 0), 0);
                  const totalSuccess = apiKeys.reduce((sum, key) => sum + (key.successToday || 0), 0);
                  return totalRequests > 0 ? Math.round((totalSuccess / totalRequests) * 100) : 0;
                })()}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Top API Keys Chart */}
      {topKeysData.length > 0 && (
        <div className="card p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Top API Keys Today</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topKeysData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="requests" fill="#3B82F6" name="Total Requests" />
              <Bar dataKey="success" fill="#10B981" name="Successful" />
              <Bar dataKey="errors" fill="#EF4444" name="Errors" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by key name, user, email, or prefix..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="all">All Keys</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* API Keys Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">API Keys ({filteredApiKeys.length})</h3>
        </div>
        
        {filteredApiKeys.length === 0 ? (
          <div className="text-center py-12">
            <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No API Keys Found</h3>
            <p className="text-gray-600">No API keys match your current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    API Key / User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Today's Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Limits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Used
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApiKeys.map((apiKey) => (
                  <tr key={apiKey.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{apiKey.name}</div>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{apiKey.keyPrefix}</code>
                        <div className="text-xs text-gray-500 mt-1">
                          {apiKey.username || apiKey.userEmail}
                          {!apiKey.userIsActive && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Inactive User
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        apiKey.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {apiKey.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div><strong>{apiKey.requestsToday || 0}</strong> requests</div>
                        <div className="text-xs text-gray-500">
                          {apiKey.requestsToday > 0
                            ? `${Math.round(((apiKey.successToday || 0) / apiKey.requestsToday) * 100)}% success`
                            : 'No activity'
                          }
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>Daily: {apiKey.dailyLimit?.toLocaleString() || 'Unlimited'}</div>
                      <div>Per min: {apiKey.minuteLimit?.toLocaleString() || 'Unlimited'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTime(apiKey.lastUsed)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(apiKey.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => viewKeyDetails(apiKey)}
                          className="btn btn-icon-sm btn-soft-info"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex space-x-1">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn btn-sm btn-secondary"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setPagination(prev => ({ ...prev, page }))}
                    className={`btn btn-sm ${
                      pagination.page === page
                        ? 'btn-primary'
                        : 'btn-secondary'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="btn btn-sm btn-secondary"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* API Key Details Modal */}
      {showDetailsModal && selectedKey && (
        <ApiKeyDetailsModal
          apiKey={selectedKey}
          usage={keyUsage}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedKey(null);
            setKeyUsage(null);
          }}
        />
      )}
    </div>
  );
};

// API Key Details Modal Component
const ApiKeyDetailsModal = ({ apiKey, usage, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">API Key Details</h3>
          <button
            onClick={onClose}
            className="btn btn-icon-sm btn-ghost"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Key Info */}
          <div>
            <h4 className="text-lg font-medium mb-4">Key Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Name</label>
                <p className="text-sm">{apiKey.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Key Prefix</label>
                <p>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">{apiKey.keyPrefix}</code>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Owner</label>
                <p className="text-sm">{apiKey.username} ({apiKey.email})</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Status</label>
                <p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    apiKey.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {apiKey.isActive ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Daily Limit</label>
                <p className="text-sm">{apiKey.dailyLimit?.toLocaleString() || 'Unlimited'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Per Minute Limit</label>
                <p className="text-sm">{apiKey.minuteLimit?.toLocaleString() || 'Unlimited'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Created</label>
                <p className="text-sm">{new Date(apiKey.createdAt).toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Last Used</label>
                <p className="text-sm">
                  {apiKey.lastUsed ? new Date(apiKey.lastUsed).toLocaleString() : 'Never'}
                </p>
              </div>
              {apiKey.expiresAt && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Expires</label>
                  <p className="text-sm">{new Date(apiKey.expiresAt).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>

          {/* Usage Stats */}
          <div>
            <h4 className="text-lg font-medium mb-4">Today's Usage</h4>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="p-3 bg-blue-50 rounded">
                  <div className="flex items-center">
                    <Activity className="w-5 h-5 text-blue-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium">Total Requests</p>
                      <p className="text-lg font-bold">{apiKey.requestsToday || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-green-50 rounded">
                  <div className="flex items-center">
                    <BarChart3 className="w-5 h-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium">Successful</p>
                      <p className="text-lg font-bold">{apiKey.successToday || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-red-50 rounded">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium">Errors</p>
                      <p className="text-lg font-bold">
                        {(apiKey.requestsToday || 0) - (apiKey.successToday || 0)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-purple-50 rounded">
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5 text-purple-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium">Success Rate</p>
                      <p className="text-lg font-bold">
                        {apiKey.requestsToday
                          ? Math.round(((apiKey.successToday || 0) / apiKey.requestsToday) * 100)
                          : 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage Percentage Bars */}
              <div>
                <h5 className="text-sm font-medium mb-2">Limit Usage</h5>
                <div className="space-y-2">
                  {apiKey.dailyLimit && (
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>Daily Usage</span>
                        <span>{Math.round(((apiKey.requestsToday || 0) / apiKey.dailyLimit) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${Math.min(100, ((apiKey.requestsToday || 0) / apiKey.dailyLimit) * 100)}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="btn btn-md btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminApiKeys;